import { jsx as _jsx } from "react/jsx-runtime";
import { useAemConfig } from '@as-react/common-components';
import { handleProductSearchObject, useSearchForProducts } from '@as-react/common-queries';
import { ABTestCookie, LISTER_TILES_DEFAULT_SIZE, MULTISELECT_FILTER, PRODUCT_FREDHOPPER_SEARCH_PARAM_PLATFORM_OPTIONS, RANGE_FILTER, } from '@as-react/common-types';
import { generateFiltersFromUrlState, HybridCookieUtil, stringifyFilters } from '@as-react/common-utils';
import { keepPreviousData } from '@tanstack/react-query';
import { stringify } from 'qs';
import { createContext, useCallback, useContext, useEffect, useMemo, useReducer } from 'react';
import { useDispatch } from 'react-redux';
import { FilterActionTypes } from '../../../enums/ActionEnums';
import { analyticsActions } from '../../../redux/analytics/actions/AnalyticsActions.creators';
import { removeFilterFromActiveFilters, updateActiveFilters } from '../utils/activeFilters.utils';
import { processFilterUpdate, updateAdditionalBoundaryDataIfNeeded, updateRangeFilterWithBoundaries, } from '../utils/filter.utils';
import { applyFilterToUrlState, getInitialUrlState, removeAllFiltersFromUrlState, removeFilterFromUrlState, } from '../utils/urlState.utils';
import { useProductListerUrlSync } from './useProductListerUrlSync';
const convertRawFilterValueUrlToCleanArray = (rawFilterValue) => {
    let resultList;
    switch (true) {
        case rawFilterValue.startsWith('('):
        case rawFilterValue.startsWith('['):
        case rawFilterValue.startsWith('{'):
            resultList = rawFilterValue.slice(1, -1).split(';');
            break;
        default:
            resultList = rawFilterValue.split(';');
            break;
    }
    return resultList;
};
export const generateFilterStateByUrlFilter = (urlFilters, filters) => urlFilters.reduce((acc, { filterOn, filterValue }) => {
    const activeFilter = generateActiveFilter(filterOn, filterValue, filters);
    if (activeFilter) {
        acc.activeFilters = Object.assign(Object.assign({}, acc.activeFilters), { [filterOn]: activeFilter });
    }
    return acc;
}, { activeFilters: {} });
const createFilteredItemsForRange = (filterValue) => convertRawFilterValueUrlToCleanArray(filterValue).map(key => ({
    filterKey: key,
}));
/**
 * Create filtered items for multiselect filters from URL filterValue
 * This parses the filterValue string (e.g., "(Nike;Adidas)") and looks up
 * display names from the available filter values.
 *
 * For SIZE filters, a single filter value may have a combined filterKey like "sz2db95e8e;sz73ae99e8"
 * representing multiple variants of the same display name (e.g., "L").
 * When parsing from URL, we need to group these keys back together.
 */
const createFilteredItemsForMultiSelectFromUrl = (filterValue, values) => {
    const parsedKeys = convertRawFilterValueUrlToCleanArray(filterValue);
    // Group parsed keys by their matching filter value
    // A filter value may have a combined filterKey containing multiple keys separated by ';'
    const groupedByFilterValue = new Map();
    for (const key of parsedKeys) {
        // First try exact match
        let matchedValue = values === null || values === void 0 ? void 0 : values.find(v => v.filterKey === key);
        // If no exact match, check if the key is part of a combined filterKey
        if (!matchedValue) {
            matchedValue = values === null || values === void 0 ? void 0 : values.find(v => {
                var _a;
                const combinedKeys = ((_a = v.filterKey) === null || _a === void 0 ? void 0 : _a.split(';')) || [];
                return combinedKeys.includes(key);
            });
        }
        const existing = groupedByFilterValue.get(matchedValue !== null && matchedValue !== void 0 ? matchedValue : null) || [];
        existing.push(key);
        groupedByFilterValue.set(matchedValue !== null && matchedValue !== void 0 ? matchedValue : null, existing);
    }
    // Create filtered items, combining keys that belong to the same filter value
    const result = [];
    for (const [filterValue, keys] of groupedByFilterValue) {
        result.push({
            filterKey: keys.join(';'),
            filterKeyName: filterValue === null || filterValue === void 0 ? void 0 : filterValue.name,
        });
    }
    return result;
};
export const isMultiSelectFilter = (filterObject) => filterObject.filterType === MULTISELECT_FILTER;
export const isRangeFilter = (filterObject) => filterObject.filterType === RANGE_FILTER;
const mapSetFilterObjectToFilteredItemWithFilterType = (setFilterObject) => {
    const data = {
        filterType: setFilterObject.filterType,
        filterOnName: setFilterObject.filterOnName,
        filterContentType: setFilterObject.filterContentType,
    };
    if (isMultiSelectFilter(setFilterObject)) {
        return Object.assign(Object.assign({}, data), { values: [...setFilterObject.filteredItems] });
    }
    if (isRangeFilter(setFilterObject)) {
        const filterKeys = setFilterObject.filteredItems.map(item => item.filterKey);
        const [rangeMinRawValue, rangeMaxRawValue] = filterKeys;
        const rangeMin = Number(rangeMinRawValue);
        const rangeMax = Number(rangeMaxRawValue);
        return Object.assign(Object.assign({}, data), { rangeMin,
            rangeMax, filterPrefix: setFilterObject.filterPrefix, filterSuffix: setFilterObject.filterSuffix });
    }
    return data;
};
const getFilterType = (type) => (type === 'RANGE' ? RANGE_FILTER : MULTISELECT_FILTER);
const generateActiveFilter = (filterOn, filterValue, filters) => {
    const usedFilter = filters === null || filters === void 0 ? void 0 : filters.filter(filter => filter.filterOn === filterOn)[0];
    if (usedFilter) {
        const filterType = getFilterType(usedFilter.type);
        const filteredItems = filterType === RANGE_FILTER
            ? createFilteredItemsForRange(filterValue)
            : createFilteredItemsForMultiSelectFromUrl(filterValue, usedFilter.values);
        const setFilterObject = {
            filterType,
            filterOn,
            filterContentType: usedFilter.filterContentType,
            filterOnName: usedFilter.name,
            filteredItems: filteredItems === null || filteredItems === void 0 ? void 0 : filteredItems.map(filteredItem => (Object.assign({}, filteredItem))),
            filterPrefix: usedFilter.filterPrefix,
            filterSuffix: usedFilter.filterSuffix,
        };
        return mapSetFilterObjectToFilteredItemWithFilterType(setFilterObject);
    }
    return null;
};
/// MOVE ABOVE to common-utils when all deprecated functions are removed
const updateExistingFilters = (filters, action, lastChangedRangeFilterOn) => {
    const newFilters = (action || []).map(filter => updateRangeFilterWithBoundaries(filter));
    filters === null || filters === void 0 ? void 0 : filters.forEach((filter) => {
        if (filter.type === FilterActionTypes.RANGE_FILTER) {
            const newFilterIndex = newFilters.findIndex(({ filterOn }) => filterOn === filter.filterOn);
            if (newFilterIndex !== -1) {
                // @ts-expect-error - boundaryMin/Max are added dynamically to FilterDTO
                const hasBoundaries = filter.boundaryMin !== undefined && filter.boundaryMax !== undefined;
                if (lastChangedRangeFilterOn === filter.filterOn && hasBoundaries) {
                    newFilters[newFilterIndex] = Object.assign(Object.assign({}, newFilters[newFilterIndex]), { 
                        // @ts-expect-error - boundaryMin/Max are added dynamically to FilterDTO
                        boundaryMin: filter.boundaryMin, 
                        // @ts-expect-error - boundaryMin/Max are added dynamically to FilterDTO
                        boundaryMax: filter.boundaryMax });
                }
                else {
                    newFilters[newFilterIndex] = updateAdditionalBoundaryDataIfNeeded(newFilters[newFilterIndex], filter);
                }
            }
        }
    });
    return newFilters;
};
/**
 * Apply optimistic active states from local activeFilters to filter items.
 * This ensures checkboxes update immediately when toggled, before the API responds.
 */
const applyOptimisticActiveStates = (filters, localActiveFilters) => filters.map(filter => {
    var _a, _b;
    if (!filter.values) {
        return filter;
    }
    const activeFilter = filter.filterOn ? localActiveFilters === null || localActiveFilters === void 0 ? void 0 : localActiveFilters[filter.filterOn] : undefined;
    // Get the set of active filter keys from local state (empty set if no active filter)
    const activeFilterKeys = new Set((_b = (_a = activeFilter === null || activeFilter === void 0 ? void 0 : activeFilter.values) === null || _a === void 0 ? void 0 : _a.map(v => v.filterKey)) !== null && _b !== void 0 ? _b : []);
    // Update the active property on each filter value based on local state
    const updatedValues = filter.values.map(value => (Object.assign(Object.assign({}, value), { active: value.filterKey ? activeFilterKeys.has(value.filterKey) : false })));
    return Object.assign(Object.assign({}, filter), { values: updatedValues });
});
// Create the context with undefined as default
const ProductListerContext = createContext(undefined);
/**
 * Hook to access the ProductLister context
 * Throws an error if used outside of ProductListerProvider
 */
export const useProductListerContext = () => {
    const context = useContext(ProductListerContext);
    if (context === undefined) {
        throw new Error('useProductListerContext must be used within a ProductListerProvider');
    }
    return context;
};
/**
 * Lister state reducer
 */
const listerReducer = (state, action) => {
    var _a;
    switch (action.type) {
        case 'SET_URL_STATE': {
            const newUrlState = Object.assign(Object.assign(Object.assign({}, state.urlState), action.payload), { defaultFilter: state.urlState.defaultFilter });
            // Regenerate activeFilters from the new URL state filters
            const newActiveFilters = ((_a = newUrlState.filter) === null || _a === void 0 ? void 0 : _a.length)
                ? generateFilterStateByUrlFilter(newUrlState.filter, state.availableFilters).activeFilters
                : undefined;
            return Object.assign(Object.assign({}, state), { urlState: newUrlState, activeFilters: newActiveFilters });
        }
        case 'SET_FILTER': {
            const setFilterObject = action.payload;
            const isRangeFilterChange = setFilterObject.filterType === RANGE_FILTER;
            // Process the filter update (handles toggle logic)
            const { filteredItems, shouldRemove } = processFilterUpdate(setFilterObject, state.activeFilters, state.availableFilters);
            if (shouldRemove) {
                // Remove the filter
                // For range filters, also clear boundaryMin/boundaryMax so they're recalculated from API
                const updatedAvailableFilters = isRangeFilterChange
                    ? state.availableFilters.map(filter => filter.filterOn === setFilterObject.filterOn
                        ? Object.assign(Object.assign({}, filter), { boundaryMin: undefined, boundaryMax: undefined }) : filter)
                    : state.availableFilters;
                return Object.assign(Object.assign({}, state), { activeFilters: removeFilterFromActiveFilters(state.activeFilters, setFilterObject.filterOn), urlState: removeFilterFromUrlState(state.urlState, setFilterObject.filterOn, setFilterObject.queryParamKey), availableFilters: updatedAvailableFilters, lastChangedRangeFilterOn: isRangeFilterChange ? setFilterObject.filterOn : undefined });
            }
            // Update with the processed filter
            const processedFilterObject = Object.assign(Object.assign({}, setFilterObject), { filteredItems });
            return Object.assign(Object.assign({}, state), { activeFilters: updateActiveFilters(state.activeFilters, processedFilterObject), urlState: applyFilterToUrlState(state.urlState, processedFilterObject), lastChangedRangeFilterOn: isRangeFilterChange ? setFilterObject.filterOn : undefined });
        }
        case 'REMOVE_FILTER':
            return Object.assign(Object.assign({}, state), { activeFilters: removeFilterFromActiveFilters(state.activeFilters, action.payload.filterOn), urlState: removeFilterFromUrlState(state.urlState, action.payload.filterOn, action.payload.queryParamKey) });
        case 'REMOVE_ALL_FILTERS':
            return Object.assign(Object.assign({}, state), { activeFilters: undefined, urlState: removeAllFiltersFromUrlState(state.urlState) });
        case 'UPDATE_PAGE':
            return Object.assign(Object.assign({}, state), { urlState: Object.assign(Object.assign({}, state.urlState), { page: action.payload }) });
        case 'UPDATE_SORT':
            return Object.assign(Object.assign({}, state), { urlState: Object.assign(Object.assign({}, state.urlState), { sort: action.payload, page: 0 }) });
        case 'UPDATE_STORE_ID':
            return Object.assign(Object.assign({}, state), { urlState: Object.assign(Object.assign({}, state.urlState), { storeId: state.urlState.storeId === action.payload ? undefined : action.payload, page: 0 }) });
        case 'SET_AVAILABLE_FILTERS':
            return Object.assign(Object.assign({}, state), { availableFilters: action.payload });
        case 'MERGE_AVAILABLE_FILTERS':
            return Object.assign(Object.assign({}, state), { availableFilters: updateExistingFilters(state.availableFilters, action.payload, state.lastChangedRangeFilterOn), 
                // Reset after merging so subsequent API responses use normal boundary logic
                lastChangedRangeFilterOn: undefined });
        case 'SET_IN_STORE_FILTER':
            return Object.assign(Object.assign({}, state), { inStoreFilter: action.payload });
        default:
            return state;
    }
};
/**
 * Get initial state for the lister reducer
 */
const getInitialState = (props, urlDefaults) => {
    var _a;
    const initialUrlState = getInitialUrlState(urlDefaults, props.initialUrlState);
    return {
        urlState: initialUrlState,
        activeFilters: props.activeFilters,
        availableFilters: ((_a = props.initialData) === null || _a === void 0 ? void 0 : _a.filters) || [],
        inStoreFilter: props.inStoreFilter,
    };
};
/**
 * ProductLister context provider
 * Manages filter state, URL synchronization, and TanStack Query integration
 */
export const ProductListerProvider = ({ children, 
// Core flags
isCSR, 
// SSR data
fredhopperGlossary = [], filterWizardConfig, inStoreFilter: initialInStoreFilter, segment, initialUrlState, initialData, activeFilters: initialActiveFilters, defaultHideFilters, 
// AEM options
title, hideTitle, seoTextInclude, enableJsonLd, quickLinks, parentPage, contentBlocks, categoryHierarchyTree, categoryLevels, partialSearchResults, }) => {
    var _a, _b;
    const reduxDispatch = useDispatch();
    const aemConfiguration = useAemConfig();
    const { mainWebShop, lang, isShopLanding, fictiveWebShop, lister: listerConfig } = aemConfiguration;
    const defaultListerSort = listerConfig.sort.defaultValue;
    const [state, dispatch] = useReducer(listerReducer, {
        isCSR,
        fredhopperGlossary,
        filterWizardConfig,
        inStoreFilter: initialInStoreFilter,
        segment,
        initialUrlState,
        initialData,
        activeFilters: initialActiveFilters,
        partialSearchResults,
        defaultHideFilters,
    }, props => getInitialState(props, { sort: defaultListerSort }));
    const { urlState, activeFilters, availableFilters, inStoreFilter, lastChangedRangeFilterOn } = state;
    // URL synchronization
    const handlePopState = useCallback((newUrlState) => {
        dispatch({ type: 'SET_URL_STATE', payload: newUrlState });
    }, []);
    const urlSyncDefaults = useMemo(() => ({ sort: defaultListerSort }), [defaultListerSort]);
    useProductListerUrlSync(urlState, {
        skipInitialPush: !isCSR, // Skip initial push for SSR
        onPopState: handlePopState,
        defaults: urlSyncDefaults,
    });
    const aiSearch = new HybridCookieUtil().hasABCookie(ABTestCookie.AI_SEARCH);
    const queryKey = stringifyFilters(Object.assign(Object.assign(Object.assign(Object.assign(Object.assign({}, (urlState.filter.length && { filter: urlState.filter })), (urlState.defaultFilter && { defaultFilter: urlState.defaultFilter })), (urlState.page >= 0 && { page: urlState.page })), (urlState.sort && { sort: urlState.sort })), (urlState.storeId && { storeId: urlState.storeId })));
    const queryResult = useSearchForProducts(Object.assign(Object.assign({ platform: isShopLanding
            ? PRODUCT_FREDHOPPER_SEARCH_PARAM_PLATFORM_OPTIONS.shoplanding
            : PRODUCT_FREDHOPPER_SEARCH_PARAM_PLATFORM_OPTIONS.public_site, mainWebShop, locale: lang, fictiveWebShop: Number(fictiveWebShop), query: urlState.q, 
        // @ts-expect-error incorrect type in common-queries
        page: urlState.page, size: LISTER_TILES_DEFAULT_SIZE }, (urlState.sort !== defaultListerSort ? { sort: [urlState.sort] } : undefined)), { aiSearch,
        segment, storeId: urlState.storeId, filter: generateFiltersFromUrlState(urlState) }), undefined, {
        query: {
            queryKey: [queryKey],
            select: data => handleProductSearchObject(data, urlState.defaultFilter),
            placeholderData: keepPreviousData,
        },
        request: {
            paramsSerializer: params => stringify(params, { arrayFormat: 'repeat' }),
        },
    });
    // Persist merged filters to state when API returns new data
    // This ensures boundaryMin/boundaryMax are preserved across filter changes
    useEffect(() => {
        var _a;
        if ((_a = queryResult.data) === null || _a === void 0 ? void 0 : _a.filters) {
            dispatch({ type: 'MERGE_AVAILABLE_FILTERS', payload: queryResult.data.filters });
        }
    }, [(_a = queryResult.data) === null || _a === void 0 ? void 0 : _a.filters]);
    const mergedFilters = updateExistingFilters(availableFilters, (_b = queryResult.data) === null || _b === void 0 ? void 0 : _b.filters, lastChangedRangeFilterOn);
    const newAvailableFilters = applyOptimisticActiveStates(mergedFilters, activeFilters);
    // Context methods - these dispatch analytics events via Redux
    const updateFilter = useCallback((toggledFilter) => {
        dispatch({ type: 'SET_FILTER', payload: toggledFilter });
    }, []);
    const removeFilter = useCallback((filterOn, queryParamKey) => {
        dispatch({ type: 'REMOVE_FILTER', payload: { filterOn, queryParamKey } });
    }, []);
    const removeAllFilters = useCallback(() => {
        dispatch({ type: 'REMOVE_ALL_FILTERS' });
    }, []);
    const updatePage = useCallback((page) => {
        dispatch({ type: 'UPDATE_PAGE', payload: page });
    }, []);
    const updateSort = useCallback((sort) => {
        dispatch({ type: 'UPDATE_SORT', payload: sort });
        // Dispatch analytics for sort change
        reduxDispatch(analyticsActions.listerSortChange(sort));
    }, [reduxDispatch]);
    const updateStoreId = useCallback((storeId) => {
        dispatch({ type: 'UPDATE_STORE_ID', payload: storeId });
    }, []);
    const updateInStoreFilter = useCallback((newInStoreFilter) => {
        dispatch({ type: 'SET_IN_STORE_FILTER', payload: newInStoreFilter });
    }, []);
    // Build context value
    const contextValue = useMemo(() => ({
        // URL State
        urlState,
        // Filter State
        filterState: {
            // activeFilters,
            activeFilters,
            availableFilters: newAvailableFilters,
        },
        // Query State
        query: queryResult,
        // Static/Config Data
        fredhopperGlossary,
        filterWizardConfig,
        inStoreFilter,
        segment,
        // Page display options
        isCSR,
        title,
        hideTitle,
        seoTextInclude,
        enableJsonLd,
        parentPage,
        quickLinks,
        contentBlocks,
        categoryHierarchyTree,
        categoryLevels,
        partialSearchResults,
        defaultHideFilters,
        // URL Filters
        urlFilters: {
            defaultFilter: urlState.defaultFilter,
            filter: urlState.filter,
        },
        // Methods
        updateFilter,
        removeFilter,
        removeAllFilters,
        updatePage,
        updateSort,
        updateStoreId,
        updateInStoreFilter,
    }), [
        urlState,
        activeFilters,
        queryResult,
        initialData,
        fredhopperGlossary,
        filterWizardConfig,
        inStoreFilter,
        segment,
        isCSR,
        title,
        hideTitle,
        seoTextInclude,
        parentPage,
        enableJsonLd,
        quickLinks,
        contentBlocks,
        categoryHierarchyTree,
        categoryLevels,
        updateFilter,
        removeFilter,
        removeAllFilters,
        updatePage,
        updateSort,
        updateStoreId,
        updateInStoreFilter,
    ]);
    return _jsx(ProductListerContext.Provider, { value: contextValue, children: children });
};
export { ProductListerContext };
